import ApplicationController from "../../../application_controller"
import { autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom"
import { highlightParts, initials, isBlank, matches, nextEnabledIndex, searchTerms, typeaheadIndex } from "./select_logic"

const SEARCH_THRESHOLD = 8
const SVG_NS = "http://www.w3.org/2000/svg"

export default class extends ApplicationController {
  static targets = ["select", "trigger", "display", "clear", "popover", "searchWrapper", "search", "list", "empty", "count", "selectAllButton", "clearButton"]
  static values = {
    multiple: Boolean,
    search: { type: String, default: "auto" },
    clearable: Boolean,
    avatar: Boolean,
    autoSubmit: Boolean,
    placeholder: String,
    classes: Object,
    messages: Object
  }

  connect() {
    this.items = []
    this.visible = []
    this.activeIndex = -1
    this.typeahead = ""
    this.onOutsidePointer = this.onOutsidePointer.bind(this)
    this.onFocusOut = this.onFocusOut.bind(this)
    this.element.addEventListener("focusout", this.onFocusOut)

    this.trackProgrammaticChanges()
    this.observer = new MutationObserver(() => this.sync())
    this.observer.observe(this.selectTarget, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["disabled", "selected", "label"] })
    this.onReset = () => setTimeout(() => this.sync())
    this.selectTarget.form?.addEventListener("reset", this.onReset)
    this.onInvalid = () => this.triggerTarget.focus()
    this.selectTarget.addEventListener("invalid", this.onInvalid)
    this.resizeObserver = new ResizeObserver(() => this.fitChips())
    this.resizeObserver.observe(this.triggerTarget)

    this.sync()
  }

  disconnect() {
    this.close({ restoreFocus: false })
    this.observer?.disconnect()
    this.resizeObserver?.disconnect()
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.selectTarget.form?.removeEventListener("reset", this.onReset)
    this.selectTarget.removeEventListener("invalid", this.onInvalid)
    delete this.selectTarget.value
    delete this.selectTarget.selectedIndex
  }

  // Other controllers set select.value directly, which fires no event; keep the trigger in step anyway.
  trackProgrammaticChanges() {
    const select = this.selectTarget
    for (const property of ["value", "selectedIndex"]) {
      const native = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, property)
      Object.defineProperty(select, property, {
        configurable: true,
        get: () => native.get.call(select),
        set: value => { native.set.call(select, value); this.sync() }
      })
    }
  }

  // ---- reading the native select ----

  readItems() {
    return Array.from(this.selectTarget.options)
      .map((option, index) => ({
        option,
        index,
        value: option.value,
        label: option.label || option.textContent.trim(),
        disabled: option.disabled,
        group: option.parentElement.tagName === "OPTGROUP" ? option.parentElement.label : null
      }))
      .filter(item => !isBlank(item) && !item.option.hidden)
  }

  selectedItems() {
    return this.readItems().filter(item => item.option.selected)
  }

  get isOpen() {
    return this.popoverTarget.matches(":popover-open")
  }

  // ---- trigger ----

  sync() {
    if (!this.hasTriggerTarget) return
    const selected = this.selectedItems()
    this.triggerTarget.disabled = this.selectTarget.disabled
    this.clearTarget.hidden = !this.clearableValue || selected.length === 0 || this.selectTarget.disabled
    this.renderDisplay(selected)
    if (this.isOpen) this.renderList()
  }

  renderDisplay(selected) {
    const nodes = []
    if (selected.length === 0) {
      nodes.push(this.span(this.classesValue.placeholder, this.placeholderValue))
    } else if (!this.multipleValue) {
      if (this.avatarValue && selected[0].value !== "") nodes.push(this.span(this.classesValue.avatar, initials(selected[0].label)))
      nodes.push(this.span(this.classesValue.value, selected[0].label))
    } else {
      selected.forEach(item => {
        const chip = this.span(this.classesValue.chip, item.label)
        chip.title = item.label
        nodes.push(chip)
      })
      nodes.push(this.span(this.classesValue.counter, ""))
    }
    this.displayTarget.replaceChildren(...nodes)
    this.fitChips()
  }

  // Shows as many chips as fit on one line and folds the rest into a "+N" counter.
  fitChips() {
    if (!this.multipleValue || !this.hasDisplayTarget) return
    const chips = Array.from(this.displayTarget.children)
    const counter = chips.pop()
    if (!counter || chips.length === 0) return

    chips.forEach(chip => { chip.hidden = false })
    counter.hidden = true
    if (this.displayTarget.clientWidth === 0) return

    let hidden = 0
    while (this.displayTarget.scrollWidth > this.displayTarget.clientWidth && hidden < chips.length - 1) {
      hidden += 1
      chips[chips.length - hidden].hidden = true
      counter.hidden = false
      counter.textContent = `+${hidden}`
    }
  }

  triggerKeydown(event) {
    const { key } = event
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(key)) {
      event.preventDefault()
      this.open()
      if (key === "ArrowUp") this.moveActive(-1, { from: -1 })
      return
    }
    if ((key === "Backspace" || key === "Delete") && this.clearableValue) {
      event.preventDefault()
      if (this.multipleValue && key === "Backspace") {
        const last = this.selectedItems().pop()
        if (last) this.toggleItem(last)
      } else {
        this.clear()
      }
      return
    }
    if (this.isPrintable(event)) {
      if (this.searchEnabled()) {
        this.open({ query: key })
        event.preventDefault()
      } else if (!this.multipleValue) {
        // Like a native select: typing picks the matching option without opening the list.
        event.preventDefault()
        const items = this.readItems()
        const current = items.findIndex(item => item.option.selected)
        const index = typeaheadIndex(items, this.bufferTypeahead(key), current)
        if (index !== -1) this.choose(items[index], { close: false })
      }
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  // ---- popover ----

  open({ query = "" } = {}) {
    if (this.isOpen || this.selectTarget.disabled) return
    this.initialValues = this.currentValues()
    this.searchWrapperTarget.hidden = !this.searchEnabled()
    this.searchTarget.value = query
    this.popoverTarget.showPopover()
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.renderList()
    this.setInitialActive()
    this.startPositioning()
    document.addEventListener("pointerdown", this.onOutsidePointer, true)
    this.focusTarget().focus({ preventScroll: true })
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return
    this.popoverTarget.hidePopover()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.cleanupPositioning?.()
    this.cleanupPositioning = null
    document.removeEventListener("pointerdown", this.onOutsidePointer, true)
    if (restoreFocus) this.triggerTarget.focus({ preventScroll: true })

    if (this.autoSubmitValue && this.multipleValue && this.currentValues() !== this.initialValues) this.submit()
  }

  startPositioning() {
    const list = this.listTarget
    this.cleanupPositioning = autoUpdate(this.triggerTarget, this.popoverTarget, () => {
      computePosition(this.triggerTarget, this.popoverTarget, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
          offset(6),
          flip({ padding: 8, fallbackAxisSideDirection: "none" }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply: ({ availableHeight, rects }) => {
              this.popoverTarget.style.minWidth = `${Math.max(rects.reference.width, 224)}px`
              const chrome = this.popoverTarget.offsetHeight - list.offsetHeight
              list.style.maxHeight = `${Math.max(Math.min(320, availableHeight - chrome), 120)}px`
            }
          })
        ]
      }).then(({ x, y }) => Object.assign(this.popoverTarget.style, { left: `${x}px`, top: `${y}px` }))
    })
  }

  onOutsidePointer(event) {
    if (!this.element.contains(event.target)) this.close({ restoreFocus: false })
  }

  onFocusOut(event) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.close({ restoreFocus: false })
  }

  // Clicking inside the list must not steal focus from the search field.
  keepFocus(event) {
    if (event.target !== this.searchTarget) event.preventDefault()
  }

  focusTarget() {
    return this.searchEnabled() ? this.searchTarget : this.listTarget
  }

  searchEnabled() {
    if (this.searchValue === "always") return true
    if (this.searchValue === "never") return false
    return this.readItems().length > SEARCH_THRESHOLD
  }

  // ---- list ----

  filter() {
    this.renderList()
    this.setActive(nextEnabledIndex(this.visible, -1, 1))
  }

  renderList() {
    this.items = this.readItems()
    const terms = searchTerms(this.searchEnabled() ? this.searchTarget.value : "")
    this.visible = this.items.filter(item => matches(item.label, terms))

    const nodes = []
    let group = null
    this.visible.forEach((item, position) => {
      if (item.group !== group) {
        group = item.group
        if (group) nodes.push(this.groupHeader(group))
      }
      nodes.push(this.optionNode(item, position, terms))
    })
    this.listTarget.replaceChildren(...nodes)
    this.listTarget.hidden = this.visible.length === 0

    this.emptyTarget.hidden = this.visible.length > 0
    this.emptyTarget.textContent = this.items.length === 0
      ? this.messagesValue.no_options
      : this.messagesValue.no_results.replace("%{query}", this.searchTarget.value.trim())

    this.renderToolbar()
    if (this.activeIndex >= this.visible.length) this.activeIndex = -1
    this.highlightActive()
  }

  groupHeader(label) {
    const header = document.createElement("li")
    header.setAttribute("role", "presentation")
    header.className = "sticky -top-1 z-10 bg-white/95 backdrop-blur px-2.5 pt-2.5 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-400"
    header.textContent = label
    return header
  }

  optionNode(item, position, terms) {
    const node = document.createElement("li")
    node.id = `${this.triggerTarget.id}-option-${item.index}`
    node.setAttribute("role", "option")
    node.setAttribute("aria-selected", String(item.option.selected))
    if (item.disabled) node.setAttribute("aria-disabled", "true")
    node.dataset.position = position
    node.className = "group/option flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-800 cursor-pointer select-none " +
      "data-active:bg-secondary-50 aria-selected:font-medium aria-disabled:opacity-40 aria-disabled:cursor-not-allowed"
    node.addEventListener("click", () => this.pick(position))
    node.addEventListener("mousemove", () => { if (this.activeIndex !== position) this.setActive(position, { scroll: false }) })

    if (this.multipleValue) node.append(this.checkbox())
    if (this.avatarValue && item.value !== "") node.append(this.span(this.classesValue.avatar, initials(item.label)))

    const label = document.createElement("span")
    label.className = "min-w-0 flex-1 truncate"
    label.title = item.label
    highlightParts(item.label, terms).forEach(part => {
      if (part.match) {
        const mark = document.createElement("mark")
        mark.className = "bg-transparent text-primary-600 font-semibold"
        mark.textContent = part.text
        label.append(mark)
      } else {
        label.append(document.createTextNode(part.text))
      }
    })
    node.append(label)

    if (!this.multipleValue) node.append(this.checkIcon("size-4 shrink-0 text-primary invisible group-aria-selected/option:visible"))
    return node
  }

  checkbox() {
    const box = document.createElement("span")
    box.className = "flex size-4 shrink-0 items-center justify-center rounded border border-gray-300 bg-white transition-colors " +
      "group-aria-selected/option:border-primary group-aria-selected/option:bg-primary"
    box.append(this.checkIcon("size-3 text-white invisible group-aria-selected/option:visible"))
    return box
  }

  checkIcon(className) {
    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    svg.setAttribute("stroke", "currentColor")
    svg.setAttribute("stroke-width", "3")
    svg.setAttribute("stroke-linecap", "round")
    svg.setAttribute("stroke-linejoin", "round")
    svg.setAttribute("aria-hidden", "true")
    svg.setAttribute("class", className)
    const path = document.createElementNS(SVG_NS, "path")
    path.setAttribute("d", "M5 12l5 5L20 7")
    svg.append(path)
    return svg
  }

  renderToolbar() {
    if (!this.multipleValue || !this.hasCountTarget) return
    const selectedCount = this.items.filter(item => item.option.selected).length
    this.countTarget.textContent = this.messagesValue.selected.replace("%{count}", selectedCount)
    const selectable = this.visible.filter(item => !item.disabled)
    this.selectAllButtonTarget.disabled = selectable.length === 0 || selectable.every(item => item.option.selected)
    this.clearButtonTarget.disabled = selectedCount === 0
  }

  setInitialActive() {
    const selected = this.visible.findIndex(item => item.option.selected)
    this.setActive(selected !== -1 ? selected : nextEnabledIndex(this.visible, -1, 1), { block: "center" })
  }

  setActive(index, { scroll = true, block = "nearest" } = {}) {
    this.activeIndex = index
    this.highlightActive()
    const node = this.activeNode()
    if (node && scroll) node.scrollIntoView({ block })
  }

  highlightActive() {
    this.listTarget.querySelectorAll("[data-active]").forEach(node => delete node.dataset.active)
    const node = this.activeNode()
    if (node) node.dataset.active = ""
    for (const target of [this.searchTarget, this.listTarget]) {
      if (node) target.setAttribute("aria-activedescendant", node.id)
      else target.removeAttribute("aria-activedescendant")
    }
  }

  activeNode() {
    return this.activeIndex === -1 ? null : this.listTarget.querySelector(`[data-position="${this.activeIndex}"]`)
  }

  moveActive(step, { from = this.activeIndex } = {}) {
    this.setActive(nextEnabledIndex(this.visible, from, step))
  }

  listKeydown(event) {
    const { key } = event
    const inSearch = event.target === this.searchTarget
    switch (key) {
      case "ArrowDown":
        event.preventDefault()
        if (event.altKey) return
        this.moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        if (event.altKey) { this.close(); return }
        this.moveActive(-1)
        break
      case "PageDown":
      case "PageUp": {
        event.preventDefault()
        const target = Math.max(0, Math.min(this.visible.length - 1, this.activeIndex + (key === "PageDown" ? 10 : -10)))
        this.setActive(nextEnabledIndex(this.visible, target - (key === "PageDown" ? 1 : -1), key === "PageDown" ? 1 : -1))
        break
      }
      case "Home":
      case "End":
        if (inSearch) return
        event.preventDefault()
        this.moveActive(key === "Home" ? 1 : -1, { from: -1 })
        break
      case "Enter":
        event.preventDefault()
        if (this.activeIndex !== -1) this.pick(this.activeIndex)
        break
      case " ":
        if (inSearch) return
        event.preventDefault()
        if (this.activeIndex !== -1) this.pick(this.activeIndex)
        break
      case "Escape":
        event.preventDefault()
        event.stopPropagation()
        if (inSearch && this.searchTarget.value) {
          this.searchTarget.value = ""
          this.filter()
        } else {
          this.close()
        }
        break
      case "Tab":
        this.close({ restoreFocus: false })
        break
      default:
        if (!inSearch && this.isPrintable(event)) {
          const index = typeaheadIndex(this.visible, this.bufferTypeahead(key), this.activeIndex)
          if (index !== -1) this.setActive(index)
        }
    }
  }

  // ---- changing the value ----

  pick(position) {
    const item = this.visible[position]
    if (!item || item.disabled) return
    this.setActive(position, { scroll: false })
    if (this.multipleValue) {
      this.toggleItem(item)
    } else {
      this.choose(item)
    }
  }

  choose(item, { close = true } = {}) {
    const changed = !item.option.selected
    item.option.selected = true
    this.sync()
    if (close) this.close()
    if (changed) {
      this.notifyChange()
      if (this.autoSubmitValue) this.submit()
    }
  }

  toggleItem(item) {
    item.option.selected = !item.option.selected
    this.sync()
    this.notifyChange()
  }

  selectAll() {
    this.visible.filter(item => !item.disabled).forEach(item => { item.option.selected = true })
    this.sync()
    this.notifyChange()
  }

  clear(event) {
    event?.preventDefault()
    if (this.multipleValue) {
      Array.from(this.selectTarget.options).forEach(option => { option.selected = false })
    } else {
      // Fall back to the blank option ("" or "All ..."), which the view renders whenever clearing is allowed.
      const blank = Array.from(this.selectTarget.options).find(option => option.value === "" && !option.disabled)
      if (!blank) return
      blank.selected = true
    }
    this.sync()
    this.notifyChange()
    if (this.autoSubmitValue && !this.isOpen) this.submit()
    if (!this.isOpen) this.triggerTarget.focus({ preventScroll: true })
  }

  notifyChange() {
    this.selectTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  submit() {
    this.selectTarget.form?.requestSubmit()
  }

  currentValues() {
    return JSON.stringify(Array.from(this.selectTarget.selectedOptions).map(option => option.value))
  }

  // ---- helpers ----

  span(className, text) {
    const span = document.createElement("span")
    span.className = className
    span.textContent = text
    return span
  }

  isPrintable(event) {
    return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && event.key !== " "
  }

  bufferTypeahead(key) {
    clearTimeout(this.typeaheadTimer)
    this.typeahead += key
    this.typeaheadTimer = setTimeout(() => { this.typeahead = "" }, 600)
    return this.typeahead
  }
}
